import React, { useState, useEffect, useMemo } from 'react';
import classNames from 'classnames/bind';
import { useRouter } from 'next/router';

import { ProductsResponse } from '@services';
import { LineItemDraft } from '@commercetools/platform-sdk';
import { Button, ScrollableProductCards } from '@components';
import { useMediaQuery, breakpoints } from 'gd-design-library';

import styles from './SmartSearchSections.module.scss';

const cx = classNames.bind(styles);

const CN = 'smart-search-sections';
const sectionClassName = `${CN}__section`;
const showMoreButtonClassName = `${CN}__show-more-button`;
const viewAllButtonClassName = `${CN}__view-all-button`;

const INITIAL_SECTIONS_COUNT = 4;

interface SmartSearchSectionsProps {
  sections: Record<string, ProductsResponse>;
  onAddToCart: (lineItemDraft: LineItemDraft, currency?: string) => Promise<void>;
  onLoadMore: (sectionTitle: string) => Promise<void>;
  isSimpleSearch: boolean;
  sectionRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  onViewAll?: (sectionTitle: string) => void;
}

export const SmartSearchSections: React.FC<SmartSearchSectionsProps> = ({
  sections,
  onAddToCart,
  onLoadMore,
  isSimpleSearch,
  sectionRefs,
  onViewAll
}) => {
  const router = useRouter();
  const isMobileOrTablet = useMediaQuery(`(max-width: ${breakpoints.md})`);
  const [ showAllSections, setShowAllSections ] = useState(false);
  const sectionsEntries = useMemo(() => Object.entries(sections), [ sections ]);
  const hasMoreThanFourSections = sectionsEntries.length > INITIAL_SECTIONS_COUNT;

  const displayedSections = useMemo(() => {
    if (isMobileOrTablet && !showAllSections && hasMoreThanFourSections) {
      return sectionsEntries.slice(0, INITIAL_SECTIONS_COUNT);
    }

    return sectionsEntries;
  }, [ isMobileOrTablet, showAllSections, hasMoreThanFourSections, sectionsEntries ]);

  useEffect(() => {
    setShowAllSections(false);
  }, [ sections ]);

  const handleViewAll = (sectionTitle: string): void => {
    if (onViewAll) {
      onViewAll(sectionTitle);
    } else {
      router.push({
        pathname: `/search/${sectionTitle}`
      });
    }
  };

  const handleShowMore = (): void => {
    setShowAllSections(true);
  };

  return (
    <>
      {displayedSections.map(([ sectionTitle, sectionData ], index) => (
        <div
          key={ sectionTitle }
          ref={ (el): void => { sectionRefs.current[sectionTitle] = el; } }
          className={ cx(sectionClassName) }
          style={ { zIndex: Object.keys(sections).length - index } }
        >
          <ScrollableProductCards
            onAddToCart={ onAddToCart }
            products={ sectionData }
            title={ !isSimpleSearch ? sectionTitle.charAt(0).toUpperCase() + sectionTitle.slice(1) : '' }
            allowQuickCart
            displayLoadMore={ sectionData.total > sectionData.results.length }
            onLoadMore={ (): Promise<void> => onLoadMore(sectionTitle) }
            displayMobileAddButton={ true }
            analyticsContext={ {
              listId: sectionTitle,
              listName: sectionTitle
            } }
            actionButton={
              <Button
                variant='inherit'
                onClick={ (): void => handleViewAll(sectionTitle) }
                className={ cx(viewAllButtonClassName) }
              >
                {isMobileOrTablet ? 'View all results' : 'View All'}
              </Button>
            }
          />
        </div>
      ))}
      {isMobileOrTablet && !showAllSections && hasMoreThanFourSections && (
        <Button
          variant='primary'
          onClick={ handleShowMore }
          className={ cx(showMoreButtonClassName) }
        >
          Show more results
        </Button>
      )}
    </>
  );
};
